import { DateTime } from 'luxon'
import { RRule } from 'rrule'

type WeekdayCode = 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU'

interface RRuleParams {
	rruleType: 'recurrent' | 'one_per_day' | 'one_time_only'
	playEvery?: number
	playEveryUnit?: 'minutes' | 'hours'
	selectedDays?: WeekdayCode[]
	timeFrom?: string
	timeTo?: string
	startDate?: Date
	endDate?: Date
}

/**
 * Builds an hour range array, wrapping across midnight when needed.
 *
 * Example:
 *  - from=23, to=1  => [23, 0, 1]
 *  - from=11, to=11 => [11]
 */
const hoursRange = (from: number, to: number): number[] => {
	if (Number.isNaN(from) || Number.isNaN(to)) return []

	if (from === to) return [from]

	const out: number[] = []
	let end = to

	// Wrap past midnight
	if (from > to) {
		end = to + 24
	}

	for (let j = from; j <= end; j++) {
		out.push(j % 24)
	}

	return out
}

/**
 * Combines a date and a "HH:mm" time string into a single Date.
 * If time is missing, it defaults to 00:00.
 */
const combineDateAndTime = (date?: Date, time?: string): Date | null => {
	if (!date) return null

	const [hhStr = '0', mmStr = '0'] = (time || '00:00').split(':')
	const hh = Number(hhStr) || 0
	const mm = Number(mmStr) || 0

	const dt = DateTime.fromJSDate(date).set({
		hour: hh,
		minute: mm,
		second: 0,
		millisecond: 0,
	})

	return dt.toJSDate()
}

const mapWeekdays = (days: WeekdayCode[] = []): any[] => {
	const map: Record<WeekdayCode, any> = {
		MO: (RRule as any).MO,
		TU: (RRule as any).TU,
		WE: (RRule as any).WE,
		TH: (RRule as any).TH,
		FR: (RRule as any).FR,
		SA: (RRule as any).SA,
		SU: (RRule as any).SU,
	}

	return days.map((d) => map[d]).filter(Boolean)
}

// Format a DateTime as a basic RRULE datetime string without hyphens:
//   20251114T000000  (no hyphens, no trailing Z)
// We deliberately omit the "Z" suffix so the value is treated as a
// timezone-agnostic "floating" local datetime and avoids shifting when
// interpreted in different timezones.
const formatRruleDate = (dt: DateTime): string =>
	dt.toFormat("yyyyLLdd'T'HHmmss")

export const generateRRule = ({
	rruleType,
	playEvery = 30,
	playEveryUnit = 'minutes',
	selectedDays = [],
	timeFrom = '00:00',
	timeTo = '23:59',
	startDate,
	endDate,
}: RRuleParams): string => {
	// Treat "no selected days" as "all days"
	const effectiveDays: WeekdayCode[] =
		selectedDays && selectedDays.length > 0
			? selectedDays
			: ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']

	// Common date boundaries
	const playFrom =
		rruleType === 'recurrent'
			? combineDateAndTime(startDate, timeFrom) // start at first window hour
			: combineDateAndTime(startDate, timeFrom) // one_per_day / one_time_only use explicit time

	const playUntil =
		endDate != null
			? DateTime.fromJSDate(endDate).endOf('day').toJSDate()
			: null

	// We treat RRULE boundaries as local datetimes for the UI and serialize
	// them as basic "floating" datetimes (no Z, no offset) to avoid
	// timezone-related date shifts when read back.
	const dtstart = playFrom || undefined
	const until = playUntil || undefined

	const byweekday = mapWeekdays(effectiveDays)

	if (rruleType === 'recurrent') {
		const [fromHour] = timeFrom.split(':').map(Number)
		const [toHour] = timeTo.split(':').map(Number)
		const byhour = hoursRange(fromHour, toHour)

		const freq =
			playEveryUnit === 'hours'
				? (RRule as any).HOURLY
				: (RRule as any).MINUTELY

		const options: any = {
			freq,
			interval: playEvery,
			wkst: (RRule as any).MO,
			byweekday,
			byhour,
		}
		const rule = new RRule(options)

		// Base RRULE without date boundaries
		const base = rule.toString().replace('RRULE:', '')

		// Append DTSTART/UNTIL as separate properties to match expected format
		const pieces = [base]

		if (dtstart) {
			const dtstartStr = formatRruleDate(DateTime.fromJSDate(dtstart))
			pieces.push(`DTSTART=${dtstartStr}`)
		}

		if (until) {
			const untilStr = formatRruleDate(DateTime.fromJSDate(until))
			pieces.push(`UNTIL=${untilStr}`)
		}

		return pieces.join(';')
	}

	if (rruleType === 'one_per_day') {
		const [hhStr = '0', mmStr = '0'] = timeFrom.split(':')
		const hh = Number(hhStr) || 0
		const mm = Number(mmStr) || 0

		const options: any = {
			freq: (RRule as any).DAILY,
			wkst: (RRule as any).MO,
			byweekday,
			byhour: hh,
			byminute: mm,
		}
		const rule = new RRule(options)

		const base = rule.toString().replace('RRULE:', '')

		const pieces = [base]

		if (dtstart) {
			const dtstartStr = formatRruleDate(DateTime.fromJSDate(dtstart))
			pieces.push(`DTSTART=${dtstartStr}`)
		}

		if (until) {
			const untilStr = formatRruleDate(DateTime.fromJSDate(until))
			pieces.push(`UNTIL=${untilStr}`)
		}

		return pieces.join(';')
	}

	// one_time_only
	const [hhStr = '0', mmStr = '0'] = timeFrom.split(':')
	const hh = Number(hhStr) || 0
	const mm = Number(mmStr) || 0
	const ss = 0

	const dtForYearDay = dtstart || playFrom
	let byyearday: number | undefined
	if (dtForYearDay) {
		const dt = DateTime.fromJSDate(dtForYearDay)
		byyearday = dt.ordinal
	}

	const options: any = {
		freq: (RRule as any).YEARLY, // 0
		count: 1,
		wkst: (RRule as any).MO,
		byhour: hh,
		byminute: mm,
		bysecond: ss,
	}

	if (byyearday != null) {
		options.byyearday = byyearday
	}

	const rule = new RRule(options)
	const base = rule.toString().replace('RRULE:', '')

	// For one-time-only we encode a single occurrence with COUNT=1 and
	// include the concrete start datetime via DTSTART so consumers can read it.
	const pieces = [base]

	if (dtstart) {
		const dtstartStr = formatRruleDate(DateTime.fromJSDate(dtstart))
		pieces.push(`DTSTART=${dtstartStr}`)
	}

	return pieces.join(';')
}
